import React from 'react';
import NodeSymbolSelect from './NodeSymbolSelect';
import { Handle, Position } from 'reactflow';

const boxStyle = {
  padding: 12, borderRadius: 8, border: '2px solid #7c3aed',
  background: 'var(--card)', minWidth: 220, fontSize: 13, color: 'var(--text)',
};
const selStyle = {
  width: '100%', marginBottom: 6, padding: 4, background: 'var(--surface)',
  color: 'var(--text)', border: '1px solid var(--border)', borderRadius: 4,
};
const rowStyle = { display: 'flex', gap: 6, marginBottom: 6 };
const numStyle = {
  width: '100%', padding: 4, background: 'var(--surface)', color: 'var(--text)',
  border: '1px solid var(--border)', borderRadius: 4, fontFamily: 'var(--font-mono)',
};

const ANCHOR_HELP = {
  rolling: 'Rolling profile over the last N bars.',
  day: "Fixed range: today's bars so far.",
  prev_day: "Fixed range: the previous trading day's full profile (constant all day).",
  session: 'Fixed range: the chosen session today, frozen once it closes.',
};

export default function VolumeProfileNode({ data, id }) {
  const onChange = (field, value) => {
    if (data.onChange) data.onChange(id, field, value);
  };
  const anchor = data.anchor || 'rolling';
  const session = data.session || 'us_open';
  return (
    <div style={boxStyle}>
      <div style={{ fontWeight: 700, marginBottom: 8, color: '#a78bfa' }}>📊 Volume Profile</div>
      <NodeSymbolSelect value={data.symbol} onChange={(v) => onChange('symbol', v)} />

      <select value={data.output || 'poc'} onChange={(e) => onChange('output', e.target.value)} style={selStyle}>
        <option value="poc">Point of Control (POC)</option>
        <option value="vah">Value Area High (VAH)</option>
        <option value="val">Value Area Low (VAL)</option>
      </select>

      <select value={anchor} onChange={(e) => onChange('anchor', e.target.value)} style={selStyle}>
        <option value="rolling">Rolling (last N bars)</option>
        <option value="day">Today (fixed range)</option>
        <option value="prev_day">Previous day (fixed range)</option>
        <option value="session">Session (fixed range)</option>
      </select>

      {anchor === 'session' && (
        <select value={session} onChange={(e) => onChange('session', e.target.value)} style={selStyle}>
          <option value="asia">Asia / Tokyo (00:00–08:00 UTC)</option>
          <option value="london">London (08:00–16:30 UTC)</option>
          <option value="us_open">New York (13:30–20:00 UTC)</option>
          <option value="custom">Custom range…</option>
        </select>
      )}
      {anchor === 'session' && session === 'custom' && (
        <div style={rowStyle}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: 'var(--muted)' }}>Start (UTC)</label>
            <input type="text" placeholder="13:30" value={data.start ?? '13:30'}
              onChange={(e) => onChange('start', e.target.value)} style={numStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, color: 'var(--muted)' }}>End (UTC)</label>
            <input type="text" placeholder="14:00" value={data.end ?? '14:00'}
              onChange={(e) => onChange('end', e.target.value)} style={numStyle} />
          </div>
        </div>
      )}

      <div style={rowStyle}>
        {anchor === 'rolling' && (<div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, color: 'var(--muted)' }}>Lookback (bars)</label>
          <input type="number" min={5} max={500} value={data.lookback ?? 50}
            onChange={(e) => onChange('lookback', Math.max(5, Math.min(500, parseInt(e.target.value, 10) || 50)))}
            style={numStyle} />
        </div>)}
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 11, color: 'var(--muted)' }}>Value area %</label>
          <input type="number" min={30} max={95} value={data.value_area_pct ?? 70}
            onChange={(e) => onChange('value_area_pct', Math.max(30, Math.min(95, parseFloat(e.target.value) || 70)))}
            style={numStyle} />
        </div>
      </div>

      <div style={{ marginTop: 2, fontSize: 10, color: 'var(--muted)' }}>
        {ANCHOR_HELP[anchor] || ANCHOR_HELP.rolling} Outputs POC / value-area level as a price —
        compare against price for acceptance / breakout logic. FX uses tick volume (approximate).
      </div>

      <Handle type="source" position={Position.Right} id="output" style={{ background: '#7c3aed' }} />
    </div>
  );
}
